/**
 * Module 133 (WP-133.1) — Daily Missions, pure.
 *
 * Three missions a UTC day (D1), the same 00:00 UTC reset as the Daily Puzzle
 * and the Daily Bonus. The set is drawn once from the pool for the player's
 * current track, seeded by the UTC date and a per-save salt (D4: Blockway has
 * no player id on Playgama, so the salt stands in for the uid), then SAVED: a
 * reload never re-rolls it, and neither does a track switch later that day
 * (D1, D3). Like the Free Coins timer (Module 123), it lives in the save and
 * reads the injected clock — no server timer.
 *
 * Progress counts only what happens after the draw (D5). It moves on the same
 * events as the milestones (`MilestoneEvent`), which already leave out SOLVE IT
 * wins and bonus moves. A mission finished but not claimed is LOST at the next
 * 00:00 UTC (user, 2026-11-02): the new day's draw replaces the whole set.
 *
 * A set whose day is LATER than today (the device clock was moved back) is kept
 * as it is and frozen — no progress, no claims — until the date catches up, so
 * moving the clock back can never re-roll or replay a day.
 */
import { MISSIONS_PER_DAY, MISSION_POOL, MISSION_SET_BONUS_TICKETS, MISSION_SOURCE_PREFIX, } from '../constants/economy';
import { mulberry32 } from '../generator/rng';
export function missionDef(id) {
    return MISSION_POOL.find((m) => m.id === id);
}
/** A fresh salt: any 32-bit integer from the given source of randomness. */
export function newMissionSalt(random) {
    return Math.floor(random() * 4294967296) >>> 0;
}
/** FNV-1a, 32-bit: the date and salt as one seed. */
function seedOf(day, salt) {
    let h = 0x811c9dc5;
    for (const ch of `${day}|${salt >>> 0}`) {
        h ^= ch.charCodeAt(0);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * Which tracks' missions a player on each track may be drawn (user,
 * 2026-11-03, revising D1): their own and the one above, never two up — an
 * Easy player at puzzle 4 was drawn "Win 2 Hard puzzles without power-ups".
 * Hard has nothing above it. Track-agnostic (`any`) missions go to everyone.
 */
const DRAWN_TRACKS = {
    beginner: ['beginner', 'intermediate'],
    intermediate: ['intermediate', 'expert'],
    expert: ['expert'],
};
/** D1 plus "doable today": may this mission be drawn for this player? */
export function isEligible(m, ctx) {
    if (m.track !== 'any' && !DRAWN_TRACKS[ctx.track].includes(m.track))
        return false;
    if (m.counter === 'dailyCleared' && ctx.dailyDoneToday)
        return false;
    if (m.counter === 'powerupsUsed' && !ctx.powerupsReachable)
        return false;
    if (m.counter === 'poweredGates' && !ctx.poweredGatesReachable)
        return false;
    return true;
}
/**
 * The day's three mission ids, in pool order (so cheaper first). Deterministic
 * in (day, salt, ctx); never two missions on the same counter (D1: no
 * duplicates, at most one per counter type).
 */
export function drawMissionIds(day, salt, ctx) {
    const eligible = MISSION_POOL.filter((m) => isEligible(m, ctx));
    const rand = mulberry32(seedOf(day, salt));
    const order = [...eligible];
    for (let i = order.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }
    const picked = [];
    for (const m of order) {
        if (picked.length >= MISSIONS_PER_DAY)
            break;
        if (picked.some((p) => p.counter === m.counter))
            continue;
        picked.push(m);
    }
    return picked.sort((a, b) => MISSION_POOL.indexOf(a) - MISSION_POOL.indexOf(b)).map((m) => m.id);
}
function drawSet(day, salt, ctx) {
    const ids = drawMissionIds(day, salt, ctx);
    return {
        day,
        ids,
        progress: ids.map(() => 0),
        claimed: ids.map(() => false),
        bonusClaimed: false,
        run: 0,
    };
}
/**
 * Today's set: the saved one while its day is today (or later — a clock moved
 * back), otherwise a new draw that replaces it. Unchanged ⇒ the same object.
 */
export function rollMissions(state, today, ctx) {
    if (state.set && state.set.day >= today)
        return state;
    return { ...state, set: drawSet(today, state.salt, ctx) };
}
/** The set counts progress and pays claims only on its own day. */
export function isLive(state, today) {
    return today !== null && state.set?.day === today;
}
/** How far one event moves one mission, given the run after the event. */
function step(m, current, evt, run) {
    switch (m.counter) {
        case 'wins':
            return evt.type === 'win' ? current + 1 : current;
        case 'trackWins':
            return evt.type === 'win' && evt.track === m.track ? current + 1 : current;
        case 'cleanTrackWins':
            return evt.type === 'win' && evt.powerupFree && evt.track === m.track ? current + 1 : current;
        case 'spareWins':
            return evt.type === 'win' && evt.movesLeft >= m.spare ? current + 1 : current;
        case 'runWins':
            return Math.max(current, run);
        case 'poweredGates':
            return evt.type === 'gatesPowered' ? current + Math.max(0, evt.count) : current;
        case 'blocksCleared':
            return evt.type === 'blocksCleared' ? current + Math.max(0, evt.count) : current;
        case 'powerupsUsed':
            return evt.type === 'powerupUsed' ? current + 1 : current;
        case 'dailyCleared':
            return evt.type === 'dailyCleared' ? current + 1 : current;
    }
}
/** Feed one event to today's set. Nothing moves unless the set is live (D5). */
export function applyMissionEvent(state, evt, today) {
    const set = state.set;
    if (!set || !isLive(state, today))
        return { state, completed: [], changed: false };
    let run = set.run;
    if (evt.type === 'win')
        run += 1;
    if (evt.type === 'attemptBroken')
        run = 0;
    const completed = [];
    let changed = run !== set.run;
    const progress = set.ids.map((id, slot) => {
        const m = missionDef(id);
        const current = set.progress[slot] ?? 0;
        if (!m || current >= m.target)
            return current;
        const next = Math.min(m.target, step(m, current, evt, run));
        if (next === current)
            return current;
        changed = true;
        if (next >= m.target)
            completed.push(slot);
        return next;
    });
    if (!changed)
        return { state, completed, changed };
    return { state: { ...state, set: { ...set, progress, run } }, completed, changed };
}
export function isMissionDone(set, slot) {
    const m = missionDef(set.ids[slot] ?? '');
    return m !== undefined && (set.progress[slot] ?? 0) >= m.target;
}
/** The server id a slot's claim is credited under: `blockjam_mission_<slot>_<coins>`. */
export function missionSourceId(slot, coins) {
    return `${MISSION_SOURCE_PREFIX}${slot + 1}_${coins}`;
}
/** Pay one finished mission, once (claimed first, so a double tap finds it paid). */
export function claimMission(state, slot, today) {
    const set = state.set;
    if (!set || !isLive(state, today))
        return null;
    const m = missionDef(set.ids[slot] ?? '');
    if (!m || set.claimed[slot] || !isMissionDone(set, slot))
        return null;
    const claimed = set.claimed.map((c, i) => c || i === slot);
    return {
        state: { ...state, set: { ...set, claimed } },
        coins: m.coins,
        sourceId: missionSourceId(slot, m.coins),
        id: m.id,
    };
}
/** D2 — all three claimed: the set bonus (free power-up uses), once a day. */
export function claimSetBonus(state, today) {
    const set = state.set;
    if (!set || !isLive(state, today) || set.bonusClaimed)
        return null;
    if (set.ids.length < MISSIONS_PER_DAY || !set.claimed.every(Boolean))
        return null;
    return {
        state: { ...state, set: { ...set, bonusClaimed: true } },
        tickets: MISSION_SET_BONUS_TICKETS,
    };
}
/** Milliseconds until the next 00:00 UTC: the popup's countdown, from the injected clock. */
export function msUntilUtcMidnight(nowMs) {
    const next = new Date(nowMs);
    next.setUTCHours(24, 0, 0, 0);
    return next.getTime() - nowMs;
}
const SALT_MAX = 4294967295;
/** The save validator's repair: sane fields kept, a broken set dropped (it is redrawn). */
export function missionStateFromRaw(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return undefined;
    const raw = value;
    const salt = raw.salt;
    if (typeof salt !== 'number' || !Number.isInteger(salt) || salt < 0 || salt > SALT_MAX) {
        return undefined;
    }
    const set = missionSetFromRaw(raw.set);
    return set ? { salt, set } : { salt };
}
function missionSetFromRaw(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return undefined;
    const raw = value;
    const { day, ids, progress, claimed, bonusClaimed, run } = raw;
    if (typeof day !== 'string' || !isUtcDate(day))
        return undefined;
    if (!Array.isArray(ids) || ids.length !== MISSIONS_PER_DAY)
        return undefined;
    const defs = ids.map((id) => (typeof id === 'string' ? missionDef(id) : undefined));
    if (defs.some((d) => d === undefined) || new Set(ids).size !== ids.length)
        return undefined;
    if (!Array.isArray(progress) || progress.length !== ids.length)
        return undefined;
    if (!Array.isArray(claimed) || claimed.length !== ids.length)
        return undefined;
    const prog = progress.map((n, i) => typeof n === 'number' && Number.isInteger(n) && n >= 0 ? Math.min(n, defs[i].target) : 0);
    // A claim the progress can't back is dropped: it never pays, it never shows paid.
    const paid = claimed.map((c, i) => c === true && prog[i] >= defs[i].target);
    return {
        day,
        ids: ids,
        progress: prog,
        claimed: paid,
        bonusClaimed: bonusClaimed === true && paid.every(Boolean),
        run: typeof run === 'number' && Number.isInteger(run) && run >= 0 ? Math.min(run, 1000) : 0,
    };
}
/** `YYYY-MM-DD` that is a real calendar day. */
function isUtcDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
